import { useEffect, useState } from "react";
import { api, fmtDate, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { ArrowCounterClockwise, Trash, Warning } from "@phosphor-icons/react";

export default function RecycleBin() {
  const { user } = useAuth();
  const tier = user?.ux_tier ?? 0;
  const canRestore = tier >= 4;
  const canPurge = tier >= 5;
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [busyKey, setBusyKey] = useState(null);
  const [err, setErr] = useState("");

  const load = async () => {
    setLoading(true); setErr("");
    try {
      const params = filter ? { entity_type: filter } : {};
      const { data } = await api.get("/recycle-bin", { params });
      setItems(data.items || []);
    } catch (e) { setErr(formatApiError(e.response?.data?.detail) || "Failed."); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const restore = async (it) => {
    if (!window.confirm(`Restore ${it.entity_type} "${it.label}"? It will reappear in the live list.`)) return;
    setBusyKey(`${it.entity_type}:${it.entity_id}`);
    try {
      await api.post(`/recycle-bin/${it.entity_type}/${encodeURIComponent(it.entity_id)}/restore`);
      await load();
    } catch (e) { alert(formatApiError(e.response?.data?.detail) || "Failed."); }
    finally { setBusyKey(null); }
  };

  const purge = async (it) => {
    const ans = window.prompt(`PERMANENT PURGE — type the label exactly to confirm:\n\n${it.label}`);
    if (ans !== it.label) { alert("Label mismatch; purge cancelled."); return; }
    setBusyKey(`${it.entity_type}:${it.entity_id}`);
    try {
      await api.delete(`/recycle-bin/${it.entity_type}/${encodeURIComponent(it.entity_id)}/purge`);
      await load();
    } catch (e) { alert(formatApiError(e.response?.data?.detail) || "Failed."); }
    finally { setBusyKey(null); }
  };

  const types = Array.from(new Set(items.map(i => i.entity_type))).sort();
  const filtered = filter ? items.filter(i => i.entity_type === filter) : items;

  return (
    <div className="space-y-6" data-testid="recycle-bin-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="label-eyebrow">Soft-deleted records</div>
          <h1 className="font-display font-bold text-[28px]">Recycle Bin</h1>
          <p className="text-[12px] text-[var(--span-text-2)] mt-1 max-w-[640px]">
            Every approved deletion lands here for <strong>2 years</strong> before automatic purge.
            {canRestore && <> COO/CEO can restore.</>}
            {canPurge && <> CEO can purge immediately with reason logged.</>}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select value={filter} onChange={(e) => setFilter(e.target.value)} className="span-input h-8 text-[11px]" data-testid="type-filter">
            <option value="">All types</option>
            {types.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
          <button onClick={load} className="span-btn h-8 text-[11px] span-btn-ghost">Refresh</button>
        </div>
      </div>

      {tier < 4 && (
        <div className="card-flat p-4 text-[12px] flex items-start gap-2" style={{ background: "rgba(247, 144, 9, 0.06)", borderColor: "#F79009" }}>
          <Warning size={14} style={{ marginTop: 2 }} />
          <div>You are viewing only the records you requested. COO/CEO see the full bin.</div>
        </div>
      )}

      {err && <div className="card-flat p-4 text-[12px] text-[var(--span-danger)]">{err}</div>}

      <div className="card-flat overflow-hidden">
        <table className="span-table" data-testid="recycle-table">
          <thead>
            <tr>
              <th>Type</th><th>Record</th><th>Deleted at</th><th>Auto-purge on</th><th>Reason</th>
              <th className="text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={6} className="text-center py-8 text-[var(--span-text-2)] font-mono text-[11px]">LOADING…</td></tr>}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={6} className="text-center py-12 text-[var(--span-text-2)] text-[12px]">Nothing in the bin.</td></tr>
            )}
            {filtered.map(it => {
              const key = `${it.entity_type}:${it.entity_id}`;
              return (
                <tr key={key} data-testid={`bin-row-${it.entity_id}`}>
                  <td><span className="badge">{it.entity_type.replace("_", " ")}</span></td>
                  <td className="font-mono text-[12px]">{it.label}</td>
                  <td className="text-[11px] font-mono text-[var(--span-text-2)]">{fmtDate(it.deleted_at)}</td>
                  <td className="text-[11px] font-mono text-[var(--span-text-2)]">{fmtDate(it.purge_at)}</td>
                  <td className="max-w-[280px] truncate text-[12px]" title={it.deletion_reason}>{it.deletion_reason}</td>
                  <td className="text-right">
                    <div className="flex items-center gap-1.5 justify-end">
                      {canRestore && (
                        <button
                          disabled={busyKey === key}
                          onClick={() => restore(it)}
                          data-testid={`restore-${it.entity_id}`}
                          className="span-btn span-btn-ghost h-7 text-[10px] disabled:opacity-50"
                        >
                          <ArrowCounterClockwise size={11} /> Restore
                        </button>
                      )}
                      {canPurge && (
                        <button
                          disabled={busyKey === key}
                          onClick={() => purge(it)}
                          data-testid={`purge-${it.entity_id}`}
                          className="span-btn h-7 text-[10px] disabled:opacity-50"
                          style={{ background: "var(--span-danger)", borderColor: "var(--span-danger)" }}
                        >
                          <Trash size={11} /> Purge
                        </button>
                      )}
                      {!canRestore && !canPurge && <span className="text-[10px] text-[var(--span-text-2)] font-mono uppercase tracking-[0.1em]">—</span>}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
